import { queryTokens, matchesAllPrefixes, messageSearchText, highlightInElement, clearHighlights } from "./search_text";
import { findMessageElement } from "./timeline_jump";

const DEBOUNCE_MS = 300;
// As in the room list: shorter words only match the loaded messages.
const MIN_SERVER_TOKEN_LENGTH = 2;
const PAGE_SIZE = 100;

const svg = (path) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${path}"/></svg>`;
export const SEARCH_ICON = svg("M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z");
const CLOSE_ICON = svg("M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z");
const OLDER_ICON = svg("M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z");
const NEWER_ICON = svg("M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z");

// Searching inside the open conversation, the way WhatsApp does it: the
// header turns into a search field with a counter and older/newer buttons,
// every match in the timeline is marked, and stepping through the matches
// jumps to each — loading history when one lies further back than what is
// loaded. Matches come from two sources, merged: the loaded messages, which
// match at word starts at once, and the search engine, which covers the whole
// history (whole words, for now).
export class RoomSearch {
  constructor(ui, room, header) {
    this.ui = ui;
    this.room = room;
    this.header = header;

    this.query = "";
    this.tokens = [];
    this.hits = []; // { eventId, ts }, newest first
    this.index = -1; // the current hit; -1 until the first one is shown
    this.cursor = null;
    this.state = "idle"; // idle, loading, done or error
    this.requestId = 0;
    this.controller = null;
    this.debounceTimer = null;
  }

  // Must run inside the tap that opens the search: iOS ignores focus()
  // anywhere else.
  open() {
    this.bar = this.buildBar();
    this.header.appendChild(this.bar);
    this.header.classList.add("bryght-chat-header--searching");
    this.input.focus();
    this.render();
  }

  close() {
    this.cancelPending();
    this.requestId += 1;
    this.bar?.remove();
    this.header.classList.remove("bryght-chat-header--searching");
    this.markCurrent(null);
    clearHighlights(document.getElementById("bryght-timeline"));
  }

  buildBar() {
    const bar = document.createElement("div");
    bar.className = "bryght-room-search-bar";
    bar.setAttribute("role", "search");

    const closeButton = this.iconButton("bryght-room-search-bar__close", CLOSE_ICON, this.ui.t("search_close", "Suche schließen"));
    closeButton.addEventListener("click", () => this.ui.closeRoomSearch());

    this.input = document.createElement("input");
    this.input.type = "search";
    this.input.className = "bryght-room-search-bar__input";
    this.input.placeholder = this.ui.t("search_in_chat", "In diesem Chat suchen");
    this.input.setAttribute("aria-label", this.input.placeholder);
    this.input.setAttribute("enterkeyhint", "search");
    this.input.autocomplete = "off";
    this.input.maxLength = 100;
    this.input.addEventListener("input", () => this.setQuery(this.input.value));
    this.input.addEventListener("keydown", (e) => this.handleKeydown(e));

    this.counter = document.createElement("span");
    this.counter.className = "bryght-room-search-bar__counter";
    this.counter.setAttribute("role", "status");
    this.counter.setAttribute("aria-live", "polite");

    this.olderButton = this.iconButton("bryght-room-search-bar__older", OLDER_ICON, this.ui.t("search_older", "Älterer Treffer"));
    this.olderButton.addEventListener("click", () => this.step(1));
    this.newerButton = this.iconButton("bryght-room-search-bar__newer", NEWER_ICON, this.ui.t("search_newer", "Neuerer Treffer"));
    this.newerButton.addEventListener("click", () => this.step(-1));

    bar.append(closeButton, this.input, this.counter, this.olderButton, this.newerButton);
    return bar;
  }

  iconButton(className, icon, label) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.title = label;
    button.setAttribute("aria-label", label);
    button.innerHTML = icon;
    return button;
  }

  handleKeydown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      this.ui.closeRoomSearch();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (this.debounceTimer) this.flush();
      else if (this.index >= 0 || this.cursor) this.step(e.shiftKey ? -1 : 1);
    }
  }

  setQuery(value) {
    const query = value.trim();
    if (query === this.query) return;

    this.query = query;
    this.tokens = queryTokens(query);
    this.cancelPending();
    this.requestId += 1;
    this.cursor = null;
    this.index = -1;
    this.hits = this.loadedHits();
    this.markCurrent(null);
    this.markTimeline();

    if (this.tokens.length) {
      this.state = this.searchesServer() ? "loading" : "done";
      this.debounceTimer = setTimeout(() => this.flush(), DEBOUNCE_MS);
    } else {
      this.state = "idle";
    }
    this.render();
  }

  searchesServer() {
    return this.tokens.some((token) => token.length >= MIN_SERVER_TOKEN_LENGTH);
  }

  cancelPending() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this.controller?.abort();
    this.controller = null;
  }

  // Typing has settled (or Enter was pressed): ask the server, then show the
  // newest match.
  flush() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    if (this.searchesServer()) this.searchServer();
    else this.showFirst();
  }

  showFirst() {
    if (this.index === -1 && this.hits.length) this.goTo(0);
  }

  matchesEvent(event) {
    return this.tokens.length > 0 &&
      event.getType() === "m.room.message" &&
      !event.isRedacted() &&
      matchesAllPrefixes(messageSearchText(event.getContent()), this.tokens);
  }

  loadedHits() {
    return this.room.getLiveTimeline().getEvents()
      .filter((event) => this.matchesEvent(event))
      .map((event) => ({ eventId: event.getId(), ts: event.getTs() }))
      .reverse();
  }

  async searchServer({ append = false } = {}) {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const requestId = ++this.requestId;

    this.state = "loading";
    this.render();

    try {
      const { hits, cursor } = await this.ui.searchEngine.searchMessages({
        term: this.query,
        tokens: this.tokens,
        roomIds: [this.room.roomId],
        cursor: append ? this.cursor : null,
        limit: PAGE_SIZE,
        signal: controller.signal
      });
      if (requestId !== this.requestId) return false;
      this.merge(hits);
      this.cursor = cursor;
      this.state = "done";
    } catch (error) {
      if (requestId !== this.requestId) return false;
      // The loaded messages still answer; the counter says the rest is missing.
      console.error("[BryghtChat] Searching the conversation failed:", error);
      this.state = "error";
    }

    this.render();
    if (!append) this.showFirst();
    return this.state === "done";
  }

  merge(serverHits) {
    const currentId = this.hits[this.index]?.eventId;
    const byId = new Map(this.hits.map((hit) => [hit.eventId, hit]));
    serverHits.forEach((hit) => {
      if (!byId.has(hit.eventId)) byId.set(hit.eventId, { eventId: hit.eventId, ts: hit.ts ?? Infinity });
    });
    this.hits = [...byId.values()].sort((a, b) => b.ts - a.ts);
    if (currentId) this.index = this.hits.findIndex((hit) => hit.eventId === currentId);
  }

  // +1 is one match older, -1 one match newer.
  async step(direction) {
    if (this.state === "loading") return;
    const next = this.index + direction;
    if (next < 0) return;
    if (next >= this.hits.length) {
      if (!this.cursor) return;
      const applied = await this.searchServer({ append: true });
      if (!applied || next >= this.hits.length) return;
    }
    this.goTo(next);
  }

  async goTo(index) {
    const hit = this.hits[index];
    this.index = index;
    this.markCurrent(null);
    this.render();

    const status = await this.ui.jumpToEvent(this.room, hit.eventId, { ts: Number.isFinite(hit.ts) ? hit.ts : null });
    // Moved on (or closed) while the history was loading.
    if (this.ui.roomSearch !== this || this.hits[this.index] !== hit) return;
    if (status === "found") this.markCurrent(findMessageElement(document.getElementById("bryght-timeline"), hit.eventId));
  }

  markTimeline() {
    const timeline = document.getElementById("bryght-timeline");
    if (!timeline) return;
    clearHighlights(timeline);
    timeline.querySelectorAll(".bryght-message").forEach((el) => this.decorate(el, { track: false }));
  }

  // Called for every message the timeline renders while the search is open —
  // on arrival, from loaded history — so it is marked and counted as well.
  decorate(wrapper, { track = true } = {}) {
    const event = wrapper?._matrixEvent;
    if (!event || !this.matchesEvent(event)) return;
    highlightInElement(wrapper, this.tokens);
    if (track) this.track(event);
  }

  track(event) {
    const eventId = event.getId();
    if (this.hits.some((hit) => hit.eventId === eventId)) return;

    const hit = { eventId, ts: event.getTs() };
    let position = this.hits.findIndex((other) => other.ts < hit.ts);
    if (position === -1) position = this.hits.length;
    this.hits.splice(position, 0, hit);
    // The current match stays the current one.
    if (this.index >= position) this.index += 1;
    this.render();
  }

  markCurrent(element) {
    const timeline = document.getElementById("bryght-timeline");
    timeline?.querySelectorAll(".bryght-message--search-current").forEach((el) => el.classList.remove("bryght-message--search-current"));
    timeline?.querySelectorAll("mark.bryght-search-mark.is-current").forEach((mark) => mark.classList.remove("is-current"));
    if (!element) return;
    element.classList.add("bryght-message--search-current");
    element.querySelectorAll("mark.bryght-search-mark").forEach((mark) => mark.classList.add("is-current"));
  }

  render() {
    if (!this.bar) return;
    const total = this.hits.length;
    this.counter.textContent = this.counterText(total);
    // A page can contain only quoted-text hits that normalization drops.
    // Its cursor still lets us reach matches on later pages, even at index -1.
    this.olderButton.disabled = this.state === "loading" || !(this.index < total - 1 || this.cursor);
    this.newerButton.disabled = this.state === "loading" || !(this.index > 0);
  }

  counterText(total) {
    if (!this.tokens.length) return "";
    if (total === 0) {
      if (this.state === "loading") return "…";
      if (this.state === "error") return this.ui.t("search_error", "Die Suche ist gerade nicht verfügbar.");
      if (this.cursor) return this.ui.t("search_jump_continue", "Weiter laden");
      return this.ui.t("search_no_matches", "Keine Treffer");
    }
    if (this.index === -1) return "…";

    const key = this.cursor ? "search_counter_more" : "search_counter";
    const fallback = this.cursor ? "%{current} von %{total}+" : "%{current} von %{total}";
    return this.ui.t(key, fallback).replace("%{current}", this.index + 1).replace("%{total}", total);
  }
}
